import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import PasswordInputWithToggle from './common/PasswordInputWithToggle';
import './NewDashboard.css';
import './AdminDashboard.css';
import './common/DataTableWithFilters.css';
import './common/DataTable.css';
import DataTable from './common/DataTable';
import { useAuth } from '../contexts/AuthContext';
import RegionalService from '../services/RegionalService';
import usuarioService from '../services/UsuarioService';
import regionalApiService from '../services/RegionalApiService';
import materiaService from '../services/MateriaService';
import areaService from '../services/AreaService';
import adminDashboardService from '../services/AdminDashboardService';
import adminAsistenteService from '../services/AdminAsistenteService';
import ProcesosMenu from './common/ProcesosMenu';
import SolicitudesProcesosBadge from './common/SolicitudesProcesosBadge';
import ChatModal from './common/ChatModal';
import {
  isAdminArea,
  isAdminRegional,
  isGptClassicEnabled as hasGptClassicFeature,
  isOpenAIAssistantEnabled as hasOpenAIAssistantFeature,
  isRagFlowAssistantEnabled as hasRagFlowAssistantFeature
} from '../utils/adminMenu';
import { getMateriaTipoLabel, MATERIA_TIPO_OPTIONS, normalizeMateriaTipo } from '../utils/materiaTypes';
import useRegionalFeatureFlags from '../hooks/useRegionalFeatureFlags';
import AdminRoleMenuOptions from './sidebar/AdminRoleMenuOptions';
import { getAdminMenuItems, navigateAdminMenuItem } from './sidebar/menuConfig';
import {
  isAssistantProcessingReady,
  isAssistantProcessingError,
  getAssistantProcessingPercent,
  getAssistantProcessingLabel,
  getAssistantProcessingClassName,
  getAssistantProcessingDetail
} from '../utils/aveProcessing';

const normalizeAssistantProvider = (value) => String(value || 'OpenAI').trim().toLowerCase();
const getAssistantId = (asistente) => asistente?.idOpenAIAssistant || asistente?.IdOpenAIAssistant || asistente?.id || asistente?.Id || '';
const isRagFlowAssistantRecord = (asistente) => normalizeAssistantProvider(asistente?.proveedorIA || asistente?.ProveedorIA) === 'ragflow';
const isGptClassicAssistantRecord = (asistente) => {
  if (!asistente) return false;
  const finalized = asistente.gptClasicoCreacionFinalizada ?? asistente.GPTClasicoCreacionFinalizada;
  return finalized != null ||
    Boolean(asistente.gptClasicoModelo || asistente.GPTClasicoModelo) ||
    Boolean(asistente.gptClasicoNombre || asistente.GPTClasicoNombre);
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const isRegionalAdmin = isAdminRegional(user);
  const isAreaAdmin = isAdminArea(user);
  const isGeneralAdmin = !isRegionalAdmin && !isAreaAdmin;
  const canManageScopedData = !isAreaAdmin;
  const canManageMaterias = canManageScopedData || isAreaAdmin;
  const [activeMenuItem, setActiveMenuItem] = useState('resumen');
  const [profesores, setProfesores] = useState([]);
  const [profesoresLoading, setProfesoresLoading] = useState(false);
  const [profesoresError, setProfesoresError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  
  const [confirmModal, setConfirmModal] = useState({ open: false, profesor: null, nextActivo: true });
  const [toggleLoading, setToggleLoading] = useState(false);
  const [resetProfesorModal, setResetProfesorModal] = useState({ open: false, profesor: null });
  const [resetProfesorLoading, setResetProfesorLoading] = useState(false);
  const [resetProfesorResult, setResetProfesorResult] = useState({ open: false, ok: true, message: '' });
  const [associateModalOpen, setAssociateModalOpen] = useState(false);
  const [associateEmail, setAssociateEmail] = useState('');
  const [associateLoading, setAssociateLoading] = useState(false);
  const [associateMessage, setAssociateMessage] = useState('');
  const [missingModal, setMissingModal] = useState({ open: false, email: '' });
  const [associateSuccess, setAssociateSuccess] = useState(false);
  const [disassociateModal, setDisassociateModal] = useState({ open: false, profesor: null });
  const [disassociateLoading, setDisassociateLoading] = useState(false);
  const [alumnos, setAlumnos] = useState([]);
  const [alumnosLoading, setAlumnosLoading] = useState(false);
  const [alumnosError, setAlumnosError] = useState('');
  const [alumnosSearch, setAlumnosSearch] = useState('');
  const [alumnoAssociateModalOpen, setAlumnoAssociateModalOpen] = useState(false);
  const [alumnoAssociateEmail, setAlumnoAssociateEmail] = useState('');
  const [alumnoAssociateLoading, setAlumnoAssociateLoading] = useState(false);
  const [alumnoAssociateMessage, setAlumnoAssociateMessage] = useState('');
  const [alumnoAssociateSuccess, setAlumnoAssociateSuccess] = useState(false);
  const [alumnoMissingModal, setAlumnoMissingModal] = useState({ open: false, email: '' });
  const [alumnoDisassociateModal, setAlumnoDisassociateModal] = useState({ open: false, alumno: null });
  const [alumnoDisassociateLoading, setAlumnoDisassociateLoading] = useState(false);
  const [alumnoToggleModal, setAlumnoToggleModal] = useState({ open: false, alumno: null, nextActivo: true });
  const [alumnoToggleLoading, setAlumnoToggleLoading] = useState(false);
  const [adminAreaUsers, setAdminAreaUsers] = useState([]);
  const [adminAreaLoading, setAdminAreaLoading] = useState(false);
  const [adminAreaError, setAdminAreaError] = useState('');
  const [adminAreaSearch, setAdminAreaSearch] = useState('');
  const [adminAreaToggleModal, setAdminAreaToggleModal] = useState({ open: false, user: null, nextActivo: true });
  const [adminAreaToggleLoading, setAdminAreaToggleLoading] = useState(false);
  const [regionalesList, setRegionalesList] = useState([]);
  const [regionalesLoading, setRegionalesLoading] = useState(false);
  const [regionalesError, setRegionalesError] = useState('');
  const [regionalesSearch, setRegionalesSearch] = useState('');
  const [regionalToggleModal, setRegionalToggleModal] = useState({ open: false, regional: null, nextActivo: true });
  const [regionalToggleLoading, setRegionalToggleLoading] = useState(false);
  const [materias, setMaterias] = useState([]);
  const [materiasLoading, setMateriasLoading] = useState(false);
  const [materiasError, setMateriasError] = useState('');
  const [materiasSearch, setMateriasSearch] = useState('');
  const [materiasFilters, setMateriasFilters] = useState({
    anio: '',
    tipo: '',
    area: ''
  });
  const [materiaToggleModal, setMateriaToggleModal] = useState({ open: false, materia: null, nextActivo: true });
  const [materiaToggleLoading, setMateriaToggleLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState('');
  const [areasSearch, setAreasSearch] = useState('');
  const [areaToggleModal, setAreaToggleModal] = useState({ open: false, area: null, nextActivo: true });
  const [areaToggleLoading, setAreaToggleLoading] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardError, setDashboardError] = useState('');
  const [asistentes, setAsistentes] = useState([]);
  const [asistentesLoading, setAsistentesLoading] = useState(false);
  const [asistentesError, setAsistentesError] = useState('');
  const [asistentesSearch, setAsistentesSearch] = useState('');
  const [profesoresPage, setProfesoresPage] = useState(1);
  const [profesoresTotal, setProfesoresTotal] = useState(0);
  const [alumnosPage, setAlumnosPage] = useState(1);
  const [alumnosTotal, setAlumnosTotal] = useState(0);
  const [adminAreaPage, setAdminAreaPage] = useState(1);
  const [adminAreaTotal, setAdminAreaTotal] = useState(0);
  const [materiasPage, setMateriasPage] = useState(1);
  const [materiasTotal, setMateriasTotal] = useState(0);
  const [areasPage, setAreasPage] = useState(1);
  const [areasTotal, setAreasTotal] = useState(0);
  const [asistentesPage, setAsistentesPage] = useState(1);
  const [asistentesTotal, setAsistentesTotal] = useState(0);
  const pageSize = 20;
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', repeat: '' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [asistenteToggleModal, setAsistenteToggleModal] = useState({ open: false, asistente: null, nextActivo: true });
  const [asistenteToggleLoading, setAsistenteToggleLoading] = useState(false);
  const userMenuRef = useRef(null);
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const activeRegionalId = useMemo(() => RegionalService.getActiveRegionalId(), []);
  const activeRegionalName = activeRegional?.nombre || activeRegional?.name || activeRegional?.descripcion || 'Regional seleccionado';
  const { featureFlags, isRefreshingFeatureFlags } = useRegionalFeatureFlags(user, { enabled: Boolean(user) });
  const isGptClassicEnabled = useMemo(() => hasGptClassicFeature(featureFlags), [featureFlags]);
  const isOpenAIAssistantEnabled = useMemo(() => hasOpenAIAssistantFeature(featureFlags), [featureFlags]);
  const isRagFlowAssistantEnabled = useMemo(() => hasRagFlowAssistantFeature(featureFlags), [featureFlags]);
  const filteredMenuItems = useMemo(
    () => getAdminMenuItems({ user, isGptClassicEnabled, isOpenAIAssistantEnabled, isRagFlowAssistantEnabled }),
    [user, isGptClassicEnabled, isOpenAIAssistantEnabled, isRagFlowAssistantEnabled]
  );
  const isRagFlowAssistantsTab = activeMenuItem === 'asistentes-ragflow';
  const assistantProviderLabel = isRagFlowAssistantsTab ? 'AVE' : 'OpenAI';
  const assistantCreatePath = isRagFlowAssistantsTab ? '/admin/asistentes-ragflow/nuevo' : '/admin/asistentes/nuevo';
  const assistantSectionIcon = 'fas fa-robot';

  const handleBackToSelectRegional = () => {
    setShowUserMenu(false);
    RegionalService.clearRegionalData();
    navigate('/admin/select-regional', { replace: true });
  };


  useEffect(() => {
    if (!activeRegionalId) {
      navigate('/admin/select-regional', { replace: true });
    }
  }, [activeRegionalId, navigate]);

  useEffect(() => {
    if (location.state?.tab) {
      const requested = location.state.tab === 'regionales' ? 'profesores' : location.state.tab;
      setActiveMenuItem(requested);
    }
  }, [location.state]);

  useEffect(() => {
    if (isRefreshingFeatureFlags) return;
    if (!filteredMenuItems.some((item) => item.id === activeMenuItem)) {
      setActiveMenuItem(filteredMenuItems[0]?.id || 'resumen');
    }
  }, [activeMenuItem, filteredMenuItems, isRefreshingFeatureFlags]);

  const getRange = useCallback((page) => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;
    return { start, end };
  }, [pageSize]);

  const clampPage = useCallback((page, total, setPage) => {
    const maxPage = Math.max(1, Math.ceil(total / pageSize));
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [pageSize]);

  const loadProfesores = useCallback(async () => {
    if (!activeRegionalId) return;

    setProfesoresLoading(true);
    setProfesoresError('');

    try {
      const { start, end } = getRange(profesoresPage);
      const response = await usuarioService.getProfesoresPorRegional(activeRegionalId, { start, end, includeTotal: true });
      const lista = response?.items || [];
      setProfesores(Array.isArray(lista) ? lista : []);
      setProfesoresTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos cargar los profesores de este regional.');
      setProfesoresTotal(0);
    } finally {
      setProfesoresLoading(false);
    }
  }, [activeRegionalId, getRange, profesoresPage]);

  const loadAlumnos = useCallback(async () => {
    if (!activeRegionalId) return;

    setAlumnosLoading(true);
    setAlumnosError('');

    try {
      const { start, end } = getRange(alumnosPage);
      const response = await usuarioService.getAlumnosPorRegional(activeRegionalId, { start, end, includeTotal: true });
      const lista = response?.items || [];
      setAlumnos(Array.isArray(lista) ? lista : []);
      setAlumnosTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      const msg = error.message || 'No pudimos cargar los alumnos de este regional.';
      const friendly = msg.toLowerCase().includes('rol alumno no configurado')
        ? 'No hay alumnos asociados a este regional.'
        : msg;
      setAlumnosError(friendly);
      setAlumnosTotal(0);
    } finally {
      setAlumnosLoading(false);
    }
  }, [activeRegionalId, getRange, alumnosPage]);

  const loadAdminAreaUsers = useCallback(async () => {
    setAdminAreaLoading(true);
    setAdminAreaError('');
    try {
      if (!activeRegionalId) {
        setAdminAreaUsers([]);
        setAdminAreaTotal(0);
        return;
      }
      const { start, end } = getRange(adminAreaPage);
      const response = await usuarioService.getAdminAreaUsersPorRegional(activeRegionalId, { start, end, includeTotal: true });
      const lista = response?.items || [];
      setAdminAreaUsers(Array.isArray(lista) ? lista : []);
      setAdminAreaTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      setAdminAreaError(error.message || 'No pudimos cargar los admins de área.');
      setAdminAreaTotal(0);
    } finally {
      setAdminAreaLoading(false);
    }
  }, [activeRegionalId, getRange, adminAreaPage]);

  const loadMaterias = useCallback(async () => {
    setMateriasLoading(true);
    setMateriasError('');
    try {
      const { start, end } = getRange(materiasPage);
      const response = await materiaService.getMaterias({ idRegional: activeRegionalId, start, end, includeTotal: true });
      const lista = response?.items || [];
      setMaterias(Array.isArray(lista) ? lista : []);
      setMateriasTotal(response?.total ?? (Array.isArray(lista) ? lista.length : 0));
    } catch (error) {
      setMateriasError(error.message || 'No pudimos cargar las materias.');
      setMateriasTotal(0);
    } finally {
      setMateriasLoading(false);
    }
  }, [activeRegionalId, getRange, materiasPage]);

  const loadAreas = useCallback(async () => {
    setAreasLoading(true);
    setAreasError('');
    try {
      const { start, end } = getRange(areasPage);
      const response = await areaService.getAreas(activeRegionalId, { start, end, includeTotal: true });
      const list = response?.items || [];
      const arr = Array.isArray(list) ? list : [];
      setAreas(arr);
      setAreasTotal(response?.total ?? arr.length);
      // sincronizar storage para formularios de materias
      RegionalService.setRegionalData({
        regionales: RegionalService.getRegionales(),
        areas: arr,
        materias: RegionalService.getMaterias()
      });
    } catch (error) {
      setAreasError(error.message || 'No pudimos cargar las áreas.');
      setAreasTotal(0);
    } finally {
      setAreasLoading(false);
    }
  }, [activeRegionalId, getRange, areasPage]);

  const loadDashboard = useCallback(async () => {
    if (!activeRegionalId) return;
    setDashboardLoading(true);
    setDashboardError('');
    try {
      const data = await adminDashboardService.getDashboard(activeRegionalId);
      setDashboardData(data || {});
    } catch (error) {
      setDashboardError(error.message || 'No pudimos cargar los indicadores.');
    } finally {
      setDashboardLoading(false);
    }
  }, [activeRegionalId]);

  const loadAsistentes = useCallback(async () => {
    setAsistentesLoading(true);
    setAsistentesError('');
    try {
      if (isRagFlowAssistantsTab) {
        await adminAsistenteService.refrescarEstadosRagFlow(activeRegionalId)
          .catch((error) => console.warn('No se pudieron refrescar estados AVE:', error));
      }

      const response = await adminAsistenteService.getAsistentes();
      const list = Array.isArray(response) ? response : response?.items || [];
      setAsistentes(Array.isArray(list) ? list : []);
      setAsistentesTotal(Array.isArray(list) ? list.length : 0);
    } catch (error) {
      setAsistentesError(error.message || 'No pudimos cargar los asistentes.');
      setAsistentesTotal(0);
    } finally {
      setAsistentesLoading(false);
    }
  }, [activeRegionalId, isRagFlowAssistantsTab]);

  useEffect(() => {
    if (activeMenuItem === 'resumen') {
      loadDashboard();
    }
  }, [activeMenuItem, loadDashboard]);

  const loadRegionales = useCallback(async () => {
    setRegionalesLoading(true);
    setRegionalesError('');
    try {
      const resp = await regionalApiService.getRegionales();
      const list = resp.regionales || resp.Regionales || resp.data || resp || [];
      setRegionalesList(Array.isArray(list) ? list : []);
    } catch (error) {
      setRegionalesError(error.message || 'No pudimos cargar los regionales.');
    } finally {
      setRegionalesLoading(false);
    }
  }, []);

  const handleMenuClick = (itemId) => {
    setActiveMenuItem(itemId);
  };

  const handleMenuSelect = (menuId) => {
    setIsSidebarOpen(false);
    if (menuId === 'asistentes-gpt' || menuId === 'reportes' || menuId === 'procesos') {
      navigateAdminMenuItem(navigate, menuId);
      return;
    }
    handleMenuClick(menuId);
  };

  // Cierral menú de usuario al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (activeMenuItem === 'profesores') {
      loadProfesores();
    } else if (activeMenuItem === 'alumnos') {
      loadAlumnos();
    } else if (activeMenuItem === 'materias') {
      loadMaterias();
    } else if (activeMenuItem === 'areas') {
      loadAreas();
    } else if (activeMenuItem === 'admin-area') {
      loadAdminAreaUsers();
    } else if (activeMenuItem === 'asistentes' || activeMenuItem === 'asistentes-ragflow') {
      loadAsistentes();
    }
  }, [activeMenuItem, loadProfesores, loadAlumnos, loadAdminAreaUsers, loadMaterias, loadAreas, loadAsistentes]);

  useEffect(() => {
    if (activeMenuItem === 'asistentes' || activeMenuItem === 'asistentes-ragflow') {
      setAsistentesPage(1);
    }
  }, [activeMenuItem]);

  useEffect(() => {
    clampPage(profesoresPage, profesoresTotal, setProfesoresPage);
  }, [profesoresPage, profesoresTotal, clampPage]);

  useEffect(() => {
    clampPage(alumnosPage, alumnosTotal, setAlumnosPage);
  }, [alumnosPage, alumnosTotal, clampPage]);

  useEffect(() => {
    clampPage(adminAreaPage, adminAreaTotal, setAdminAreaPage);
  }, [adminAreaPage, adminAreaTotal, clampPage]);

  useEffect(() => {
    clampPage(materiasPage, materiasTotal, setMateriasPage);
  }, [materiasPage, materiasTotal, clampPage]);

  useEffect(() => {
    clampPage(areasPage, areasTotal, setAreasPage);
  }, [areasPage, areasTotal, clampPage]);

  useEffect(() => {
    clampPage(asistentesPage, asistentesTotal, setAsistentesPage);
  }, [asistentesPage, asistentesTotal, clampPage]);

  const filteredProfesores = useMemo(() => {
    if (!searchTerm.trim()) return profesores;
    const term = searchTerm.toLowerCase();

    return profesores.filter((prof) => {
      const fullName = `${prof.nombre || prof.Nombre || ''} ${prof.apellido || prof.Apellido || ''} ${prof.displayName || prof.DisplayName || ''}`.toLowerCase();
      const email = (prof.email || prof.Email || '').toLowerCase();
      const dni = (prof.dni || prof.Dni || '').toLowerCase();
      return fullName.includes(term) || email.includes(term) || dni.includes(term);
    });
  }, [profesores, searchTerm]);

  const providerAsistentes = useMemo(() => {
    return asistentes.filter((asistente) => {
      if (isGptClassicAssistantRecord(asistente)) return false;
      const isRagFlow = isRagFlowAssistantRecord(asistente);
      return isRagFlowAssistantsTab ? isRagFlow : !isRagFlow;
    });
  }, [asistentes, isRagFlowAssistantsTab]);

  const filteredAsistentes = useMemo(() => {
    if (!asistentesSearch.trim()) return providerAsistentes;
    const term = asistentesSearch.toLowerCase();
    return providerAsistentes.filter((a) => {
      const nombre = (a.nombre || a.Nombre || '').toLowerCase();
      const tipo = (a.nombreTipoAsistente || a.NombreTipoAsistente || '').toLowerCase();
      const area = (a.nombreArea || a.NombreArea || '').toLowerCase();
      const materia = (a.nombreMateria || a.NombreMateria || '').toLowerCase();
      const procesamiento = getAssistantProcessingLabel(a).toLowerCase();
      const id = getAssistantId(a).toLowerCase();
      return (
        nombre.includes(term) ||
        tipo.includes(term) ||
        area.includes(term) ||
        materia.includes(term) ||
        procesamiento.includes(term) ||
        id.includes(term)
      );
    });
  }, [providerAsistentes, asistentesSearch]);

  const paginatedAsistentes = useMemo(() => {
    const start = (asistentesPage - 1) * pageSize;
    return filteredAsistentes.slice(start, start + pageSize);
  }, [asistentesPage, filteredAsistentes, pageSize]);

  useEffect(() => {
    clampPage(asistentesPage, filteredAsistentes.length, setAsistentesPage);
  }, [asistentesPage, filteredAsistentes.length, clampPage]);

  const paginate = (list, page) => {
    return Array.isArray(list) ? list : [];
  };

  const renderPagination = (totalItems, page, setPage) => {
    if (totalItems <= 0) return null;
    const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

    const currentPage = Math.min(Math.max(page, 1), totalPages);
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    const buttons = [];

    if (startPage > 1 && totalPages > 1) {
      buttons.push(
        <button key="first" className="pagination-btn" onClick={() => setPage(1)}>
          1
        </button>
      );
      if (startPage > 2) {
        buttons.push(<span key="ellipsis1" className="pagination-ellipsis">...</span>);
      }
    }

    if (totalPages > 1) {
      for (let i = startPage; i <= endPage; i++) {
        buttons.push(
          <button
            key={i}
            className={`pagination-btn ${i === currentPage ? 'active' : ''}`}
            onClick={() => setPage(i)}
          >
            {i}
          </button>
        );
      }
    } else {
      buttons.push(
        <button key="single" className="pagination-btn active" disabled>
          1
        </button>
      );
    }

    if (endPage < totalPages && totalPages > 1) {
      if (endPage < totalPages - 1) {
        buttons.push(<span key="ellipsis2" className="pagination-ellipsis">...</span>);
      }
      buttons.push(
        <button key="last" className="pagination-btn" onClick={() => setPage(totalPages)}>
          {totalPages}
        </button>
      );
    }

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
      <div className="pagination-container">
        <div className="pagination-info">
          Mostrando {startItem}-{endItem} de {totalItems}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn nav-btn"
            onClick={() => setPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1 || totalPages === 1}
            aria-label="Pagina anterior"
          >
            <i className="fas fa-chevron-left" />
          </button>
          {buttons}
          <button
            className="pagination-btn nav-btn"
            onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages || totalPages === 1}
            aria-label="Pagina siguiente"
          >
            <i className="fas fa-chevron-right" />
          </button>
        </div>
      </div>
    );
  };

  const renderAssistantProcessingStatus = (assistant) => {
    const label = getAssistantProcessingLabel(assistant);
    const percent = getAssistantProcessingPercent(assistant);
    const detail = getAssistantProcessingDetail(assistant);
    const className = getAssistantProcessingClassName(assistant);

    return (
      <div className="ave-processing-cell" title={detail || label}>
        <span className={`ave-processing-badge ${className}`}>{label}</span>
        {percent !== null && (
          <div className="ave-processing-meta">
            <div className="ave-processing-bar" aria-hidden="true">
              <span style={{ width: `${percent}%` }} />
            </div>
            <small>{percent}%</small>
          </div>
        )}
      </div>
    );
  };

  const getProfesorNombre = (prof) => {
    return prof.displayName || prof.DisplayName || `${prof.nombre || prof.Nombre || ''} ${prof.apellido || prof.Apellido || ''}`.trim() || prof.email || prof.Email || 'Sin nombre';
  };

  const filteredAlumnos = useMemo(() => {
    if (!alumnosSearch.trim()) return alumnos;
    const term = alumnosSearch.toLowerCase();

    return alumnos.filter((al) => {
      const fullName = `${al.nombre || al.Nombre || ''} ${al.apellido || al.Apellido || ''} ${al.displayName || al.DisplayName || ''}`.toLowerCase();
      const email = (al.email || al.Email || '').toLowerCase();
      const legajo = (al.legajo || al.Legajo || '').toLowerCase();
      return fullName.includes(term) || email.includes(term) || legajo.includes(term);
    });
  }, [alumnos, alumnosSearch]);

  const getAlumnoNombre = (al) => {
    return al.displayName || al.DisplayName || `${al.nombre || al.Nombre || ''} ${al.apellido || al.Apellido || ''}`.trim() || al.email || al.Email || 'Sin nombre';
  };

  const filteredRegionales = useMemo(() => {
    if (!regionalesSearch.trim()) return regionalesList;
    const term = regionalesSearch.toLowerCase();
    return regionalesList.filter((r) => {
      const name = (r.nombre || r.Nombre || '').toLowerCase();
      const desc = (r.descripcion || r.Descripcion || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [regionalesList, regionalesSearch]);

  const getRegionalNombre = (r) => r.nombre || r.Nombre || r.descripcion || r.Descripcion || 'Regional';

  const materiaYearOptions = useMemo(() => {
    const values = Array.from(new Set(
      materias
        .map((m) => m.valorAnio ?? m.ValorAnio)
        .filter((value) => value !== null && value !== undefined && value !== '')
    ));

    return values
      .sort((a, b) => Number(a) - Number(b))
      .map((value) => ({ value: String(value), label: `${value}` }));
  }, [materias]);

  const materiaAreaOptions = useMemo(() => {
    const values = Array.from(new Set(
      materias
        .map((m) => m.nombreArea || m.areaNombre || m.NombreArea || m.AreaNombre || m.area || m.Area || '')
        .filter(Boolean)
    ));

    return values
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
      .map((value) => ({ value, label: value }));
  }, [materias]);

  const filteredMaterias = useMemo(() => {
    const term = materiasSearch.toLowerCase();

    if (!term && !materiasFilters.anio && !materiasFilters.tipo && !materiasFilters.area) {
      return materias;
    }

    return materias.filter((m) => {
      const name = (m.nombre || m.Nombre || '').toLowerCase();
      const areaValue = m.nombreArea || m.areaNombre || m.NombreArea || m.AreaNombre || m.area || m.Area || '';
      const area = areaValue.toLowerCase();
      const codigo = (m.codigo || m.Codigo || '').toLowerCase();
      const anioValue = m.valorAnio ?? m.ValorAnio;
      const anio = String(anioValue ?? '').toLowerCase();
      const tipoMateriaValue = normalizeMateriaTipo(m.tipoMateria || m.TipoMateria);
      const tipoMateria = tipoMateriaValue.toLowerCase();

      const matchesSearch = !term || name.includes(term) || area.includes(term) || codigo.includes(term) || anio.includes(term) || tipoMateria.includes(term);
      const matchesAnio = !materiasFilters.anio || String(anioValue ?? '') === materiasFilters.anio;
      const matchesTipo = !materiasFilters.tipo || tipoMateriaValue === materiasFilters.tipo;
      const matchesArea = !materiasFilters.area || areaValue === materiasFilters.area;

      return matchesSearch && matchesAnio && matchesTipo && matchesArea;
    });
  }, [materias, materiasFilters.anio, materiasFilters.area, materiasFilters.tipo, materiasSearch]);

  const getMateriaNombre = (m) => m.nombre || m.Nombre || 'Materia';

  const handleMateriaFilterChange = (field, value) => {
    setMateriasFilters((prev) => ({ ...prev, [field]: value }));
  };

  const clearMateriaFilters = () => {
    setMateriasSearch('');
    setMateriasFilters({
      anio: '',
      tipo: '',
      area: ''
    });
  };

  const filteredAreas = useMemo(() => {
    if (!areasSearch.trim()) return areas;
    const term = areasSearch.toLowerCase();
    return areas.filter((a) => {
      const name = (a.nombre || a.Nombre || '').toLowerCase();
      const desc = (a.descripcion || a.Descripcion || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [areas, areasSearch]);

  const getAreaNombre = (a) => a.nombre || a.Nombre || 'Área';

  const filteredAdminAreaUsers = useMemo(() => {
    if (!adminAreaSearch.trim()) return adminAreaUsers;
    const term = adminAreaSearch.toLowerCase();
    return adminAreaUsers.filter((u) => {
      const name = `${u.nombre || u.Nombre || ''} ${u.apellido || u.Apellido || ''} ${u.displayName || u.DisplayName || ''}`.toLowerCase();
      const email = (u.email || u.Email || '').toLowerCase();
      return name.includes(term) || email.includes(term);
    });
  }, [adminAreaUsers, adminAreaSearch]);

  const getResultMessage = (result = {}) => {
    return result.mensaje || result.Mensaje || result.message || result.Message || result.error || '';
  };

  const isProfesorActivo = (prof) => {
    if (typeof prof.activo === 'boolean') return prof.activo;
    if (typeof prof.Activo === 'boolean') return prof.Activo;
    return true;
  };

  const isAlumnoActivo = (al) => {
    if (typeof al.activo === 'boolean') return al.activo;
    if (typeof al.Activo === 'boolean') return al.Activo;
    return true;
  };

  const isAdminAreaActivo = (adminUser) => {
    if (typeof adminUser.activo === 'boolean') return adminUser.activo;
    if (typeof adminUser.Activo === 'boolean') return adminUser.Activo;
    return true;
  };

  const openToggleModal = (prof) => {
    const currentActive = isProfesorActivo(prof);
    setConfirmModal({
      open: true,
      profesor: prof,
      nextActivo: !currentActive
    });
  };

  const closeToggleModal = () => {
    setConfirmModal({ open: false, profesor: null, nextActivo: true });
  };

  const handleToggleProfesor = async () => {
    if (!confirmModal.profesor) return;
    const profesorId = confirmModal.profesor.id || confirmModal.profesor.Id;
    const nextActivo = confirmModal.nextActivo;

    if (!profesorId) {
      setProfesoresError('No pudimos identificar al profesor seleccionado.');
      closeToggleModal();
      return;
    }

    setToggleLoading(true);
    setProfesoresError('');

    try {
      await usuarioService.updateProfesorActivo(profesorId, nextActivo);

      setProfesores((prev) =>
        prev.map((p) => {
          const pid = p.id || p.Id;
          if (pid === profesorId) {
            return { ...p, activo: nextActivo, Activo: nextActivo };
          }
          return p;
        })
      );
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos actualizar el estado del profesor.');
    } finally {
      setToggleLoading(false);
      closeToggleModal();
    }
  };

  const openDisassociateModal = (prof) => {
    setDisassociateModal({ open: true, profesor: prof });
  };

  const closeDisassociateModal = () => {
    setDisassociateModal({ open: false, profesor: null });
  };

  const handleDisassociate = async () => {
    if (!disassociateModal.profesor) return;
    const profesorId = disassociateModal.profesor.id || disassociateModal.profesor.Id;

    if (!profesorId || !activeRegionalId) {
      setProfesoresError('No pudimos identificar al profesor o el regional.');
      closeDisassociateModal();
      return;
    }

    setDisassociateLoading(true);
    setProfesoresError('');

    try {
      await usuarioService.desasociarProfesorDeRegional(profesorId, activeRegionalId);
      await loadProfesores();
    } catch (error) {
      setProfesoresError(error.message || 'No pudimos desasociar al profesor.');
    } finally {
      setDisassociateLoading(false);
      closeDisassociateModal();
    }
  };

  const openResetProfesorModal = (prof) => {
    setResetProfesorModal({ open: true, profesor: prof });
  };

  const closeResetProfesorModal = () => {
    setResetProfesorModal({ open: false, profesor: null });
    setResetProfesorLoading(false);
  };

  const handleResetProfesorPassword = async () => {
    if (!resetProfesorModal.profesor) return;
    const profesorId = resetProfesorModal.profesor.id || resetProfesorModal.profesor.Id;

    if (!profesorId) {
      setResetProfesorResult({ open: true, ok: false, message: 'No pudimos identificar al profesor seleccionado.' });
      closeResetProfesorModal();
      return;
    }

    setResetProfesorLoading(true);
    try {
      await usuarioService.resetProfesorPassword(profesorId);
      setResetProfesorResult({ open: true, ok: true, message: 'Contraseña reseteada: ahora será su DNI.' });
    } catch (error) {
      setResetProfesorResult({ open: true, ok: false, message: error.message || 'No pudimos resetear la contraseña.' });
    } finally {
      setResetProfesorLoading(false);
      closeResetProfesorModal();
    }
  };

  // Alumnos - acciones
  const openAlumnoAssociateModal = () => {
    setAlumnoAssociateModalOpen(true);
    setAlumnoAssociateEmail('');
    setAlumnoAssociateMessage('');
    setAlumnoAssociateSuccess(false);
  };

  const closeAlumnoAssociateModal = () => {
    setAlumnoAssociateModalOpen(false);
    setAlumnoAssociateLoading(false);
    setAlumnoAssociateMessage('');
    setAlumnoAssociateSuccess(false);
  };

  const handleValidateAndAssociateAlumno = async () => {
    if (!alumnoAssociateEmail.trim()) {
      setAlumnoAssociateMessage('Debes ingresar un email.');
      return;
    }
    if (!activeRegionalId) {
      setAlumnoAssociateMessage('Selecciona un regional antes de asociar alumnos.');
      return;
    }

    setAlumnoAssociateLoading(true);
    setAlumnoAssociateMessage('');

    try {
      const result = await usuarioService.validateAlumnoPorEmail(alumnoAssociateEmail.trim(), activeRegionalId);
    const message = getResultMessage(result) || 'Validación completada.';
    const asociado = result.asociado ?? result.Asociado ?? false;
    const asociacionCreada = result.asociacionCreada ?? result.AsociacionCreada ?? false;

      if (!asociado && !result.alumnoExiste && !result.AlumnoExiste) {
        closeAlumnoAssociateModal();
        setAlumnoMissingModal({ open: true, email: alumnoAssociateEmail.trim() });
        return;
      }

      if (asociado && asociacionCreada) {
        setAlumnoAssociateMessage('Alumno asociado exitosamente');
        setAlumnoAssociateSuccess(true);
        await loadAlumnos();
      } else if (asociado && !asociacionCreada) {
        setAlumnoAssociateMessage(message || 'El alumno ya está asociado al regional.');
        setAlumnoAssociateSuccess(true);
      } else {
        setAlumnoAssociateMessage(message);
      }
    } catch (error) {
      setAlumnoAssociateMessage(error.message || 'No pudimos validar al alumno.');
    } finally {
      setAlumnoAssociateLoading(false);
    }
  };

  const openAlumnoDisassociateModal = (al) => {
    setAlumnoDisassociateModal({ open: true, alumno: al });
  };

  const closeAlumnoDisassociateModal = () => {
    setAlumnoDisassociateModal({ open: false, alumno: null });
  };

  const handleAlumnoDisassociate = async () => {
    if (!alumnoDisassociateModal.alumno) return;
    const alumnoId = alumnoDisassociateModal.alumno.id || alumnoDisassociateModal.alumno.Id;

    if (!alumnoId || !activeRegionalId) {
      setAlumnosError('No pudimos identificar al alumno o el regional.');
      closeAlumnoDisassociateModal();
      return;
    }

    setAlumnoDisassociateLoading(true);
    setAlumnosError('');

    try {
      await usuarioService.desasociarAlumnoDeRegional(alumnoId, activeRegionalId);
      await loadAlumnos();
    } catch (error) {
      setAlumnosError(error.message || 'No pudimos desasociar al alumno.');
    } finally {
      setAlumnoDisassociateLoading(false);
      closeAlumnoDisassociateModal();
    }
  };

  const handleEditAlumno = (al) => {
    const alumnoId = al.id || al.Id;
    if (!alumnoId) {
      setAlumnosError('No pudimos identificar al alumno seleccionado.');
      return;
    }

    navigate(`/admin/alumnos/${alumnoId}/editar`, {
      replace: false,
      state: {
        alumno: al,
        idRegional: activeRegionalId
      }
    });
  };

  const openAdminAreaToggleModal = (user) => {
    const currentActive = isAdminAreaActivo(user);
    setAdminAreaToggleModal({
      open: true,
      user,
      nextActivo: !currentActive
    });
  };

  const closeAdminAreaToggleModal = () => {
    setAdminAreaToggleModal({ open: false, user: null, nextActivo: true });
  };

  const handleToggleAdminArea = async () => {
    if (!adminAreaToggleModal.user) return;
    const userId = adminAreaToggleModal.user.id || adminAreaToggleModal.user.Id;
    const nextActivo = adminAreaToggleModal.nextActivo;

    if (!userId) {
      setAdminAreaError('No pudimos identificar al usuario de área.');
      closeAdminAreaToggleModal();
      return;
    }

    setAdminAreaToggleLoading(true);
    try {
      await usuarioService.updateAdminAreaActivo(userId, nextActivo);

      setAdminAreaUsers((prev) =>
        prev.map((u) => {
          const uid = u.id || u.Id;
          if (uid === userId) {
            return { ...u, activo: nextActivo, Activo: nextActivo };
          }
          return u;
        })
      );
    } catch (error) {
      setAdminAreaError(error.message || 'No pudimos actualizar el estado del usuario.');
    } finally {
      setAdminAreaToggleLoading(false);
      closeAdminAreaToggleModal();
    }
  };

  const handleEditAdminArea = (user) => {
    const userId = user.id || user.Id || 'nuevo';
    navigate(`/admin/admin-area/${userId}/editar`, {
      replace: false,
      state: {
        adminAreaUser: user,
        idRegional: activeRegionalId
      }
    });
  };

  const handleCreateAdminArea = () => {
    navigate('/admin/admin-area/nuevo', {
      replace: false,
      state: {
        idRegional: activeRegionalId
      }
    });
  };

  // Áreas acciones
  const handleCreateArea = () => {
    navigate('/admin/areas/nuevo', {
      replace: false,
      state: {
        idRegional: activeRegionalId
      }
    });
  };

  const handleEditArea = (area) => {
    const areaId = area.id || area.Id || 'nuevo';
    navigate(`/admin/areas/${areaId}/editar`, {
      replace: false,
      state: {
        area,
        idRegional: activeRegionalId
      }
    });
  };

  const openAreaToggleModal = (area) => {
    const currentActive = typeof area.activo === 'boolean' ? area.activo : typeof area.Activo === 'boolean' ? area.Activo : true;
    setAreaToggleModal({ open: true, area, nextActivo: !currentActive });
  };

  const closeAreaToggleModal = () => {
    setAreaToggleModal({ open: false, area: null, nextActivo: true });
  };

  const handleToggleArea = async () => {
    if (!areaToggleModal.area) return;
    const areaId = areaToggleModal.area.id || areaToggleModal.area.Id;
    const nextActivo = areaToggleModal.nextActivo;

    if (!areaId) {
      setAreasError('No pudimos identificar el área seleccionada.');
      closeAreaToggleModal();
      return;
    }

    setAreaToggleLoading(true);
    setAreasError('');

    try {
      await areaService.updateAreaActivo(areaId, nextActivo);

      setAreas((prev) =>
        prev.map((a) => {
          const aid = a.id || a.Id;
          if (aid === areaId) {
            return { ...a, activo: nextActivo, Activo: nextActivo };
          }
          return a;
        })
      );
    } catch (error) {
      setAreasError(error.message || 'No pudimos actualizar el estado del área.');
    } finally {
      setAreaToggleLoading(false);
      closeAreaToggleModal();
    }
  };

  // Materias acciones (placeholder hasta integrar endpoints)
  const handleCreateMateria = () => {
    navigate('/admin/materias/nuevo', {
      replace: false,
      state: {
        idRegional: activeRegionalId
      }
    });
  };

  const handleEditMateria = (materia) => {
    const materiaId = materia.id || materia.Id || 'nuevo';
    navigate(`/admin/materias/${materiaId}/editar`, {
      replace: false,
      state: {
        materia,
        idRegional: activeRegionalId
      }
    });
  };

  const openMateriaToggleModal = (materia) => {
    const currentActive = typeof materia.activo === 'boolean' ? materia.activo : typeof materia.Activo === 'boolean' ? materia.Activo : true;
    setMateriaToggleModal({
      open: true,
      materia,
      nextActivo: !currentActive
    });
  };

  const closeMateriaToggleModal = () => {
    setMateriaToggleModal({ open: false, materia: null, nextActivo: true });
  };

  const handleToggleMateria = async () => {
    if (!materiaToggleModal.materia) return;
    const materiaId = materiaToggleModal.materia.id || materiaToggleModal.materia.Id;
    const nextActivo = materiaToggleModal.nextActivo;

    if (!materiaId) {
      setMateriasError('No pudimos identificar la materia seleccionada.');
      closeMateriaToggleModal();
      return;
    }

    setMateriaToggleLoading(true);
    setMateriasError('');

    try {
      await materiaService.updateMateriaActivo(materiaId, nextActivo);

      setMaterias((prev) =>
        prev.map((m) => {
          const mid = m.id || m.Id;
          if (mid === materiaId) {
            return { ...m, activo: nextActivo, Activo: nextActivo };
          }
          return m;
        })
      );
    } catch (error) {
      setMateriasError(error.message || 'No pudimos actualizar la materia.');
    } finally {
      setMateriaToggleLoading(false);
      closeMateriaToggleModal();
    }
  };

  // Regionales acciones
  const openRegionalToggleModal = (regional) => {
    const currentActive = typeof regional.activo === 'boolean' ? regional.activo : typeof regional.Activo === 'boolean' ? regional.Activo : true;
    setRegionalToggleModal({
      open: true,
      regional,
      nextActivo: !currentActive
    });
  };

  const closeRegionalToggleModal = () => {
    setRegionalToggleModal({ open: false, regional: null, nextActivo: true });
  };

  const handleToggleRegional = async () => {
    if (!regionalToggleModal.regional) return;
    const regId = regionalToggleModal.regional.id || regionalToggleModal.regional.Id;
    const nextActivo = regionalToggleModal.nextActivo;

    if (!regId) {
      setRegionalesError('No pudimos identificar el regional.');
      closeRegionalToggleModal();
      return;
    }

    setRegionalToggleLoading(true);
    setRegionalesError('');

    try {
      await regionalApiService.updateRegional(regId, {
        nombre: regionalToggleModal.regional.nombre || regionalToggleModal.regional.Nombre,
        descripcion: regionalToggleModal.regional.descripcion || regionalToggleModal.regional.Descripcion,
        activo: nextActivo
      });

      setRegionalesList((prev) =>
        prev.map((r) => {
          const rid = r.id || r.Id;
          if (rid === regId) {
            return { ...r, activo: nextActivo, Activo: nextActivo };
          }
          return r;
        })
      );
    } catch (error) {
      setRegionalesError(error.message || 'No pudimos actualizar el regional.');
    } finally {
      setRegionalToggleLoading(false);
      closeRegionalToggleModal();
    }
  };

  const handleEditRegional = (regional) => {
    const regId = regional.id || regional.Id;
    if (!regId) {
      setRegionalesError('No pudimos identificar el regional seleccionado.');
      return;
    }

    navigate(`/admin/regionales/${regId}/editar`, {
      replace: false,
      state: {
        regional,
        idRegional: activeRegionalId
      }
    });
  };

  const handleCreateRegional = () => {
    navigate('/admin/regionales/nuevo', { replace: false });
  };

  const openAlumnoToggleModal = (al) => {
    const currentActive = isAlumnoActivo(al);
    setAlumnoToggleModal({
      open: true,
      alumno: al,
      nextActivo: !currentActive
    });
  };

  const closeAlumnoToggleModal = () => {
    setAlumnoToggleModal({ open: false, alumno: null, nextActivo: true });
  };

  const handleToggleAlumno = async () => {
    if (!alumnoToggleModal.alumno) return;
    const alumnoId = alumnoToggleModal.alumno.id || alumnoToggleModal.alumno.Id;
    const nextActivo = alumnoToggleModal.nextActivo;

    if (!alumnoId) {
      setAlumnosError('No pudimos identificar al alumno seleccionado.');
      closeAlumnoToggleModal();
      return;
    }

    setAlumnoToggleLoading(true);
    setAlumnosError('');

    try {
      await usuarioService.updateAlumnoActivo(alumnoId, nextActivo);

      setAlumnos((prev) =>
        prev.map((a) => {
          const aid = a.id || a.Id;
          if (aid === alumnoId) {
            return { ...a, activo: nextActivo, Activo: nextActivo };
          }
          return a;
        })
      );
    } catch (error) {
      setAlumnosError(error.message || 'No pudimos actualizar el estado del alumno.');
    } finally {
      setAlumnoToggleLoading(false);
      closeAlumnoToggleModal();
    }
  };

  const handleEditProfesor = (prof) => {
    const profesorId = prof.id || prof.Id;
    if (!profesorId) {
      setProfesoresError('No pudimos identificar al profesor seleccionado.');
      return;
    }

    navigate(`/admin/profesores/${profesorId}/editar`, {
      replace: false,
      state: {
        profesor: prof,
        idRegional: activeRegionalId
      }
    });
  };

  const openAssociateModal = () => {
    setAssociateModalOpen(true);
    setAssociateEmail('');
    setAssociateMessage('');
    setAssociateSuccess(false);
  };

  const closeAssociateModal = () => {
    setAssociateModalOpen(false);
    setAssociateLoading(false);
    setAssociateMessage('');
    setAssociateSuccess(false);
  };

  const handleValidateAndAssociate = async () => {
    if (!associateEmail.trim()) {
      setAssociateMessage('Debes ingresar un email.');
      return;
    }
    if (!activeRegionalId) {
      setAssociateMessage('Selecciona un regional antes de asociar profesores.');
      return;
    }

    setAssociateLoading(true);
    setAssociateMessage('');

    try {
      const result = await usuarioService.validateProfesorPorEmail(associateEmail.trim(), activeRegionalId);
      const message = getResultMessage(result) || 'Validación completada.';
      const asociado = result.asociado ?? result.Asociado ?? false;
      const asociacionCreada = result.asociacionCreada ?? result.AsociacionCreada ?? false;

      if (!asociado && !result.profesorExiste && !result.ProfesorExiste) {
        closeAssociateModal();
        setMissingModal({ open: true, email: associateEmail.trim() });
        return;
      }

      if (asociado && asociacionCreada) {
        setAssociateMessage('Profesor asociado exitosamente');
        setAssociateSuccess(true);
        await loadProfesores();
      } else if (asociado && !asociacionCreada) {
        setAssociateMessage(message || 'El profesor ya está asociado al regional.');
        setAssociateSuccess(true);
      } else {
        setAssociateMessage(message);
      }
    } catch (error) {
      setAssociateMessage(error.message || 'No pudimos validar al profesor.');
    } finally {
      setAssociateLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!passwordForm.current || !passwordForm.next || !passwordForm.repeat) {
      setPasswordMessage('Completa todos los campos.');
      return;
    }
    if (passwordForm.next !== passwordForm.repeat) {
      setPasswordMessage('Las nuevas contraseñas no coinciden.');
      return;
    }
    setPasswordLoading(true);
    setPasswordMessage('Actualizando contraseña...');
    try {
      await usuarioService.updatePassword(passwordForm.current, passwordForm.next, passwordForm.repeat);
      setPasswordMessage('Contraseña actualizada correctamente.');
      setTimeout(() => {
        setPasswordModalOpen(false);
        setPasswordForm({ current: '', next: '', repeat: '' });
        setPasswordMessage('');
      }, 1000);
    } catch (error) {
      setPasswordMessage(error.message || 'No pudimos actualizar la contraseña.');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleTestAssistant = (assistant) => {
    if (isRagFlowAssistantRecord(assistant) && !isAssistantProcessingReady(assistant)) {
      return;
    }

    setSelectedAssistant(assistant);
    setShowChatModal(true);
  };

  const handleCloseChatModal = () => {
    setShowChatModal(false);
    setSelectedAssistant(null);
  };

  const handleOpenAsistenteToggle = (asistente) => {
    if (isRagFlowAssistantRecord(asistente) && !isAssistantProcessingReady(asistente) && !isAssistantProcessingError(asistente)) {
      return;
    }

    const activo = asistente.activo ?? asistente.Activo ?? true;
    setAsistenteToggleModal({ open: true, asistente, nextActivo: !activo, error: '' });
  };

  const handleConfirmToggleAsistente = async () => {
    setAsistenteToggleLoading(true);
    try {
      const asistente = asistenteToggleModal.asistente;
      if (!asistente) throw new Error('No se encontró el asistente.');
      const id = asistente.idOpenAIAssistant || asistente.IdOpenAIAssistant || asistente.id || asistente.Id;
      if (!id) throw new Error('Falta el identificador del asistente.');
      const res = await adminAsistenteService.updateAsistenteActivo(id, asistenteToggleModal.nextActivo);
      const nuevoEstado = res.activo ?? res.Activo ?? asistenteToggleModal.nextActivo;
      setAsistentes((prev) =>
        (prev || []).map((a) =>
          (a.idOpenAIAssistant || a.IdOpenAIAssistant || a.id || a.Id) === id
            ? { ...a, activo: nuevoEstado, Activo: nuevoEstado }
            : a
        )
      );
      setAsistenteToggleModal({ open: false, asistente: null, nextActivo: true });
    } catch (error) {
      setAsistenteToggleModal((prev) => ({ ...prev, error: error.message || 'No pudimos cambiar el estado.' }));
    } finally {
      setAsistenteToggleLoading(false);
    }
  };

  const renderDashboardSection = () => {
    const data = dashboardData || {};
    const topAsistentesRaw = data.topAsistentes || data.TopAsistentes || [];
    const conversacionesRecientesRaw = data.conversacionesRecientes || data.ConversacionesRecientes || data.threadsRecientes || data.ThreadsRecientes || [];
    const topAsistentes = Array.isArray(topAsistentesRaw) ? topAsistentesRaw : [];
    const conversacionesRecientes = Array.isArray(conversacionesRecientesRaw) ? conversacionesRecientesRaw : [];

    const totalUsuarios = data.totalUsuarios ?? data.TotalUsuarios ?? 0;
    const totalProfesores = data.totalProfesores ?? data.TotalProfesores ?? 0;
    const totalAgentesGpts = data.totalAgentesGpts ?? data.TotalAgentesGpts ?? data.totalAsistentesAsignados ?? data.TotalAsistentesAsignados ?? 0;
    const totalAreas = data.totalAreas ?? data.TotalAreas ?? 0;
    const totalAreasActivas = data.totalAreasActivas ?? data.TotalAreasActivas ?? 0;
    const totalMaterias = data.totalMaterias ?? data.TotalMaterias ?? 0;
    const totalMateriasActivas = data.totalMateriasActivas ?? data.TotalMateriasActivas ?? 0;
    const totalAsistentesAsignados = data.totalAsistentesAsignados ?? data.TotalAsistentesAsignados ?? 0;
    const totalAsistentesActivos = data.totalAsistentesActivos ?? data.TotalAsistentesActivos ?? 0;
    const totalConversaciones = data.totalConversaciones ?? data.TotalConversaciones ?? data.totalThreads ?? data.TotalThreads ?? 0;
    const conversacionesHoy = data.conversacionesHoy ?? data.ConversacionesHoy ?? data.threadsHoy ?? data.ThreadsHoy ?? 0;
    const conversacionesSemana = data.conversacionesSemana ?? data.ConversacionesSemana ?? data.threadsSemana ?? data.ThreadsSemana ?? 0;
    const conversacionesMes = data.conversacionesMes ?? data.ConversacionesMes ?? data.threadsMes ?? data.ThreadsMes ?? 0;

    const ratio = (a, b) => {
      if (!b || b <= 0) return 0;
      const v = Math.round((a / b) * 100);
      return Number.isFinite(v) ? v : 0;
    };

    const conversacionesMax = Math.max(conversacionesHoy, conversacionesSemana, conversacionesMes, 1);
    const scopeSummary = isAreaAdmin
      ? `Panorama general de tus áreas en ${data.nombreRegional || data.NombreRegional || activeRegionalName}.`
      : `Panorama general del regional ${data.nombreRegional || data.NombreRegional || activeRegionalName}.`;

    const quickCards = [
      { label: 'Profesores', value: totalProfesores, icon: 'fa-chalkboard-teacher', color: '#0ea5e9' },
      { label: 'Agentes GPTS', value: totalAgentesGpts, icon: 'fa-robot', color: '#8b5cf6' },
      { label: 'Usuarios totales', value: totalUsuarios, icon: 'fa-users', color: '#10b981' },
    ];

    return (
      <div className="dashboard-home">
        <div
          className="content-card"
          style={{
            background: 'linear-gradient(135deg, #0f172a, #1e293b)',
            color: '#e2e8f0',
            border: 'none',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)'
          }}
        >
          <div className="card-header" style={{ borderColor: 'rgba(255,255,255,0.08)', marginBottom: '0.25rem' }}>
            <div className="card-title" style={{ color: '#e2e8f0' }}>
              <i className="fas fa-chart-line" />
              <span>Resumen de {data.nombreRegional || data.NombreRegional || activeRegionalName}</span>
            </div>
            <div className="professor-actions" style={{ gap: '0.5rem' }}>
              <button
                className="refresh-btn"
                type="button"
                onClick={loadDashboard}
                disabled={dashboardLoading}
                style={{
                  color: '#f8fafc',
                  border: '1px solid rgba(255,255,255,0.25)',
                  background: 'rgba(255,255,255,0.08)',
                  borderRadius: '10px',
                  padding: '0.45rem 0.65rem'
                }}
              >
                <i className={`fas ${dashboardLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              </button>
            </div>
          </div>
          <div className="card-content" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1.25rem' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.6rem', color: '#f8fafc' }}>Bienvenido, {user?.displayName || user?.name || user?.email}</h2>
              <p style={{ margin: '0.35rem 0 1rem', color: '#cbd5e1' }}>
                {scopeSummary}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                {quickCards.map((card) => (
                  <div
                    key={card.label}
                    style={{
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderRadius: '14px',
                      padding: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <div style={{ width: 42, height: 42, borderRadius: '12px', background: `${card.color}20`, display: 'grid', placeItems: 'center', color: card.color }}>
                      <i className={`fas ${card.icon}`} />
                    </div>
                    <div>
                      <p style={{ margin: 0, color: '#cbd5e1', fontSize: '0.9rem' }}>{card.label}</p>
                      <p style={{ margin: 0, color: '#f8fafc', fontSize: '1.25rem', fontWeight: 700 }}>{card.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', padding: '1rem' }}>
              <p style={{ margin: '0 0 0.35rem', color: '#cbd5e1', fontWeight: 600 }}>Actividad de agentes GPT</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <div>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Configurados</p>
                  <p style={{ margin: 0, fontSize: '1.3rem', color: '#f8fafc', fontWeight: 700 }}>{totalAsistentesAsignados}</p>
                </div>
                <div>
                  <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.85rem' }}>Activos</p>
                  <p style={{ margin: 0, fontSize: '1.3rem', color: '#22c55e', fontWeight: 700 }}>{totalAsistentesActivos}</p>
                </div>
              </div>

              <div style={{ marginTop: '0.5rem' }}>
                <p style={{ margin: 0, color: '#cbd5e1', fontWeight: 600 }}>Conversaciones</p>
                <p style={{ margin: '0 0 0.6rem', color: '#94a3b8', fontSize: '0.9rem' }}>Total: {totalConversaciones}</p>
                {[
                  { label: 'Hoy', value: conversacionesHoy, color: '#0ea5e9' },
                  { label: 'Semana', value: conversacionesSemana, color: '#8b5cf6' },
                  { label: 'Mes', value: conversacionesMes, color: '#22c55e' },
                ].map((item) => (
                  <div key={item.label} style={{ marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#cbd5e1', fontSize: '0.9rem' }}>
                      <span>{item.label}</span>
                      <span>{item.value}</span>
                    </div>
                    <div style={{ width: '100%', height: 8, borderRadius: 999, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                      <div style={{ width: `${Math.min(100, Math.round((item.value / conversacionesMax) * 100))}%`, height: '100%', background: item.color }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-layer-group" />
              <span>Estado general</span>
            </div>
            <div className="professor-actions" style={{ gap: '0.35rem' }}>
              <button className="primary-btn" type="button" onClick={() => setActiveMenuItem('profesores')}>
                <i className="fas fa-chalkboard-teacher" />
                <span>Ir a profesores</span>
              </button>
              <button
                className="primary-btn outline"
                type="button"
                onClick={() => setActiveMenuItem('materias')}
                style={{ border: '1px solid var(--brand-primary)', color: 'var(--brand-primary)' }}
              >
                <i className="fas fa-book" />
                <span>Ir a materias</span>
              </button>
            </div>
          </div>

          <div className="card-content" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            {[
              { label: 'Áreas activas', value: `${totalAreasActivas} / ${totalAreas}`, percent: ratio(totalAreasActivas, totalAreas), color: '#0ea5e9' },
              { label: 'Materias activas', value: `${totalMateriasActivas} / ${totalMaterias}`, percent: ratio(totalMateriasActivas, totalMaterias), color: '#f97316' },
              { label: 'Agentes GPT activos', value: `${totalAsistentesActivos} / ${totalAsistentesAsignados}`, percent: ratio(totalAsistentesActivos, totalAsistentesAsignados), color: '#22c55e' },
              { label: 'Usuarios totales', value: totalUsuarios, percent: 100, color: '#6366f1' },
            ].map((item) => (
              <div
                key={item.label}
                style={{
                  border: '1px solid var(--gray-200)',
                  borderRadius: '14px',
                  padding: '0.85rem',
                  background: '#f8fafc'
                }}
              >
                <p style={{ margin: 0, color: '#475569', fontWeight: 600 }}>{item.label}</p>
                <p style={{ margin: '0.15rem 0 0.35rem', fontSize: '1.2rem', fontWeight: 700, color: '#0f172a' }}>{item.value}</p>
                <div style={{ width: '100%', height: 8, borderRadius: 999, background: '#e2e8f0', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(100, item.percent)}%`, height: '100%', background: item.color }}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-robot" />
              <span>Top agentes GPT</span>
            </div>
          </div>
          <div className="card-content">
            <DataTable
              data={topAsistentes.map((item, idx) => {
                const name = item.nombre || item.Nombre || item.displayName || item.DisplayName || item.asistente || item.Asistente || `Asistente ${idx + 1}`;
                const conversaciones = item.totalConversaciones || item.TotalConversaciones || item.totalThreads || item.TotalThreads || item.uso || item.Uso || item.total || item.Total || item.cantidad || item.Cantidad || 0;
                const ultimoUsoRaw = item.ultimaConversacion || item.UltimaConversacion || item.ultimoThread || item.UltimoThread || item.ultimoUso || item.UltimoUso;
                let ultimoUso = '—';
                if (ultimoUsoRaw) {
                  const d = new Date(ultimoUsoRaw);
                  if (!isNaN(d.getTime())) {
                    ultimoUso = d.toLocaleString();
                  }
                }
                return { name, conversaciones, ultimoUso };
              })}
              columns={[
                {
                  key: 'name',
                  title: 'Agente GPT',
                  render: (value) => (
                    <span className="table-cell">
                      <i className="fas fa-microchip" style={{ marginRight: '0.4rem', color: '#0ea5e9' }} />
                      {value}
                    </span>
                  )
                },
                { key: 'conversaciones', title: 'Conversaciones', align: 'center' },
                { key: 'ultimoUso', title: 'Último uso', align: 'center' }
              ]}
              emptyMessage="Aún no hay actividad de agentes GPT en este alcance."
              className="dashboard-home-table"
            />
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-stream" />
              <span>Conversaciones recientes</span>
            </div>
          </div>
          <div className="card-content">
            <DataTable
              data={conversacionesRecientes.map((conversacion, idx) => {
                const title = conversacion.titulo || conversacion.Titulo || conversacion.conversationId || conversacion.ConversationId || conversacion.idConversacion || conversacion.IdConversacion || conversacion.idOpenAIThread || conversacion.IdOpenAIThread || `Conversación ${idx + 1}`;
                const assistant = conversacion.nombreAsistente || conversacion.NombreAsistente || 'N/D';
                const fechaRaw = conversacion.ultimaActividad || conversacion.UltimaActividad || conversacion.fecha || conversacion.Fecha || conversacion.fechaCreacion || conversacion.FechaCreacion || conversacion.createdAt || conversacion.CreatedAt;
                let fechaFmt = fechaRaw || '—';
                if (fechaRaw) {
                  const d = new Date(fechaRaw);
                  if (!isNaN(d.getTime())) {
                    fechaFmt = d.toLocaleString();
                  }
                }
                return { title, assistant, fechaFmt };
              })}
              columns={[
                { key: 'title', title: 'Conversación' },
                { key: 'assistant', title: 'Asistente' },
                { key: 'fechaFmt', title: 'Última actividad' }
              ]}
              emptyMessage="Sin conversaciones recientes."
              className="dashboard-home-table"
            />
          </div>
        </div>
      </div>
    );
  };

  const renderProfesoresSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Profesores</p>
          <h2>Profesores en {activeRegionalName}</h2>
          <p className="subtitle">Listado filtrable de docentes asociados a este regional.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-users"></i>
            </div>
            <div>
              <p className="count-label">Total docentes</p>
              <p className="count-number">{profesoresTotal}</p>
            </div>
          </div>
          {canManageScopedData && (
            <button className="primary-btn" type="button" onClick={openAssociateModal}>
              <i className="fas fa-user-plus" />
              <span>Asociar nuevo profesor</span>
            </button>
          )}
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-chalkboard-teacher"></i>
            <span>Listado de profesores</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o DNI"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setProfesoresPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadProfesores} disabled={profesoresLoading}>
              <i className={`fas ${profesoresLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>
        <div className="card-content professors-table">
          {profesoresLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando profesores de {activeRegionalName}...</p>
            </div>
          )}

          {!profesoresLoading && profesoresError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{profesoresError}</p>
              <button className="refresh-btn" type="button" onClick={loadProfesores}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!profesoresLoading && !profesoresError && filteredProfesores.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No se encontraron profesores que coincidan con tu búsqueda.</p>
            </div>
          )}

          {!profesoresLoading && !profesoresError && filteredProfesores.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>DNI</th>
                    <th>Teléfono</th>
                    <th>Materias</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredProfesores, profesoresPage).map((prof) => {
                    const nombre = getProfesorNombre(prof);
                    const email = prof.email || prof.Email || '-';
                    const dni = prof.dni || prof.Dni || '-';
                    const telefono = prof.telefono || prof.Telefono || '-';
                    const activo = isProfesorActivo(prof);
                    const materias = Array.isArray(prof.materias || prof.Materias) ? (prof.materias || prof.Materias) : [];
                    const materiasLabel = materias
                      .map((materia) => materia.nombre || materia.Nombre)
                      .filter(Boolean)
                      .join(', ');

                    return (
                      <tr key={prof.id || prof.Id || email}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                            {(dni && dni !== '-') && <span className="muted">DNI {dni}</span>}
                          </div>
                        </td>
                        <td>{email}</td>
                        <td>{dni}</td>
                        <td>{telefono}</td>
                        <td>
                          <span className="muted">{materiasLabel || '—'}</span>
                        </td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          {canManageScopedData ? (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="action-edit-btn"
                                onClick={() => handleEditProfesor(prof)}
                                title="Editar profesor"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className="action-reset-btn"
                                onClick={() => openResetProfesorModal(prof)}
                                title="Resetear contraseña"
                              >
                                <i className="fas fa-key"></i>
                              </button>
                              <button
                                type="button"
                                className="action-remove-btn"
                                onClick={() => openDisassociateModal(prof)}
                                title="Desasociar profesor del regional"
                              >
                                <i className="fas fa-link-slash"></i>
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                                onClick={() => openToggleModal(prof)}
                                disabled={toggleLoading}
                                title={activo ? 'Desactivar profesor' : 'Activar profesor'}
                              >
                                <i className={`fas ${activo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                              </button>
                            </div>
                          ) : (
                            <span className="muted">Solo lectura</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!profesoresLoading && !profesoresError && profesoresTotal > 0 && (
            renderPagination(profesoresTotal, profesoresPage, setProfesoresPage)
          )}
        </div>
      </div>

            {associateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Asociar profesor</h3>
            <p>
              Primero validaremos si el docente está registrado y/o asociado al regional {activeRegionalName}.
            </p>
            <div className="form-field">
              <label>Email del profesor</label>
              <input
                type="email"
                placeholder="profesor@ejemplo.com"
                value={associateEmail}
                onChange={(e) => setAssociateEmail(e.target.value)}
              />
            </div>
            {associateMessage && (
              <div className="modal-message">
                {associateMessage}
              </div>
            )}
            <div className="modal-actions">
              {associateSuccess ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={closeAssociateModal}
                >
                  <i className="fas fa-check" />
                  <span>Ok</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeAssociateModal}
                    disabled={associateLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-primary success"
                    onClick={handleValidateAndAssociate}
                    disabled={associateLoading}
                  >
                    {associateLoading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-check" />
                    )}
                    <span>Validar y asociar</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${confirmModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
            </div>
            <h3>
              {confirmModal.nextActivo ? 'Activar profesor' : 'Desactivar profesor'}
            </h3>
            <p>
              {confirmModal.nextActivo
                ? `A continuación se activará el profesor ${getProfesorNombre(confirmModal.profesor)}, permitiendo que pueda iniciar sesión en el sistema. ¿Está de acuerdo?`
                : `A continuación se desactivará el profesor ${getProfesorNombre(confirmModal.profesor)}, por lo tanto no podrá iniciar sesión en el sistema. ¿Está de acuerdo?`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeToggleModal}
                disabled={toggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${confirmModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleProfesor}
                disabled={toggleLoading}
              >
                {toggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${confirmModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
                )}
                <span>{confirmModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {missingModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Profesor no encontrado</h3>
            <p>
              El profesor {missingModal.email} no existe en el sistema. ¿Desea continuar con la carga de dicho profesor?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setMissingModal({ open: false, email: '' })}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={() => {
                  const emailToUse = missingModal.email;
                  setMissingModal({ open: false, email: '' });
                  navigate('/admin/profesores/nuevo', {
                    replace: false,
                    state: {
                      email: emailToUse,
                      idRegional: activeRegionalId
                    }
                  });
                }}
              >
                <i className="fas fa-arrow-right" />
                <span>Continuar con alta</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {disassociateModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-unlink" />
            </div>
            <h3>Desasociar profesor</h3>
            <p>
              El profesor {getProfesorNombre(disassociateModal.profesor)} dejará de estar asociado al regional {activeRegionalName}. No podrá ver ni gestionar contenidos de este regional hasta que se lo asocie nuevamente.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeDisassociateModal}
                disabled={disassociateLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={handleDisassociate}
                disabled={disassociateLoading}
              >
                {disassociateLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-unlink" />}
                <span>Desasociar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {resetProfesorModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-key" />
            </div>
            <h3>Resetear contraseña</h3>
            <p>
              Se va a resetear el password al profesor {getProfesorNombre(resetProfesorModal.profesor)} y será su DNI.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeResetProfesorModal} disabled={resetProfesorLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleResetProfesorPassword} disabled={resetProfesorLoading}>
                {resetProfesorLoading ? 'Reseteando...' : 'Resetear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetProfesorResult.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${resetProfesorResult.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
            </div>
            <h3>{resetProfesorResult.ok ? 'Contraseña reseteada' : 'No se pudo resetear'}</h3>
            <p>{resetProfesorResult.message}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setResetProfesorResult({ open: false, ok: true, message: '' })}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAsistentesSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Asistentes {assistantProviderLabel}</p>
          <h2>Asistentes {assistantProviderLabel} en {activeRegionalName}</h2>
          <p className="subtitle">Gestiona los asistentes IA {assistantProviderLabel} asociados a este regional.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className={assistantSectionIcon}></i>
            </div>
            <div>
              <p className="count-label">Total {assistantProviderLabel}</p>
              <p className="count-number">{providerAsistentes.length}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={() => navigate(assistantCreatePath)}>
            <i className="fas fa-plus" />
            <span>Nuevo {assistantProviderLabel}</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className={assistantSectionIcon}></i>
            <span>Listado de asistentes {assistantProviderLabel}</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre, tipo, área o materia"
                value={asistentesSearch}
                onChange={(e) => { setAsistentesSearch(e.target.value); setAsistentesPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAsistentes} disabled={asistentesLoading}>
              <i className={`fas ${asistentesLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>
        <div className="card-content professors-table">
          {asistentesLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando asistentes {assistantProviderLabel} de {activeRegionalName}...</p>
            </div>
          )}

          {!asistentesLoading && asistentesError && (
            <div className="data-table-error">
              <i className="fas fa-exclamation-triangle"></i>
              <p>{asistentesError}</p>
              <button className="primary-btn" type="button" onClick={loadAsistentes}>
                <i className="fas fa-sync-alt"></i>
                <span>Reintentar</span>
              </button>
            </div>
          )}

          {!asistentesLoading && !asistentesError && filteredAsistentes.length === 0 && (
            <div className="data-table-empty">
              <i className="fas fa-box-open"></i>
              <p>No hay asistentes {assistantProviderLabel} registrados en este regional.</p>
          {canManageScopedData && (
            <button className="primary-btn" type="button" onClick={() => navigate(assistantCreatePath)}>
              <i className="fas fa-plus"></i>
              <span>Crear {assistantProviderLabel}</span>
            </button>
          )}
            </div>
          )}

          {!asistentesLoading && !asistentesError && filteredAsistentes.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Tipo</th>
                    <th>Área</th>
                    <th>Materia</th>
                    <th>Fecha alta</th>
                    <th>Password</th>
                    {isRagFlowAssistantsTab && <th className="text-center width-md">Procesamiento</th>}
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAsistentes.map((asistente, idx) => {
                const nombre = asistente.nombre || asistente.Nombre || 'N/D';
                const tipo = asistente.nombreTipoAsistente || asistente.NombreTipoAsistente || asistente.idTipoAsistente || 'N/D';
                const area = asistente.nombreArea || asistente.NombreArea || '—';
                const materia = asistente.nombreMateria || asistente.NombreMateria || '—';
                const fecha = asistente.fechaAlta || asistente.FechaAlta;
                const password = asistente.passwordAsistente || asistente.PasswordAsistente || '—';
                const activo = asistente.activo ?? asistente.Activo ?? true;
                const assistantId = getAssistantId(asistente);
                const isRagFlowRecord = isRagFlowAssistantRecord(asistente);
                const hasProcessingError = isAssistantProcessingError(asistente);
                const canUseAssistantActions = !isRagFlowRecord || isAssistantProcessingReady(asistente);
                const editBasePath = isRagFlowRecord ? '/admin/asistentes-ragflow' : '/admin/asistentes';
                let fechaFmt = fecha || '';
                if (fecha) {
                  const d = new Date(fecha);
                  if (!isNaN(d.getTime())) {
                    fechaFmt = d.toLocaleDateString();
                      }
                    }
                    const key = assistantId || `asistente-${idx}`;
                    return (
                      <tr key={key}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                            <span className="muted">{assistantId || '—'}</span>
                          </div>
                        </td>
                        <td>{tipo}</td>
                        <td>{area}</td>
                        <td>{materia}</td>
                        <td>{fechaFmt || '—'}</td>
                        <td>{password}</td>
                        {isRagFlowAssistantsTab && (
                          <td className="text-center">
                            {renderAssistantProcessingStatus(asistente)}
                          </td>
                        )}
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          {canManageScopedData ? (
                            canUseAssistantActions ? (
                              <div className="table-actions">
                                <button
                                  className="table-btn table-btn-edit"
                                  title="Editar"
                                  onClick={() => navigate(`${editBasePath}/${assistantId}/editar`)}
                                  disabled={!assistantId}
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  className="table-btn table-btn-test"
                                  title="Probar asistente"
                                  onClick={() => handleTestAssistant(asistente)}
                                >
                                  <i className="fas fa-play"></i>
                                </button>
                                <button
                                  className="table-btn table-btn-reports"
                                  title="Ver reportes"
                                  onClick={() => navigate('/reports', { state: { assistant: asistente } })}
                                >
                                  <i className="fas fa-chart-bar"></i>
                                </button>
                                <button
                                  className={`table-btn ${activo ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
                                  title={activo ? 'Desactivar asistente' : 'Activar asistente'}
                                  onClick={() => handleOpenAsistenteToggle(asistente)}
                                >
                                  <i className={`fas ${activo ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                </button>
                              </div>
                            ) : (
                              hasProcessingError ? (
                                <div className="table-actions">
                                  <button
                                    className={`table-btn ${activo ? 'table-btn-toggle-inactive' : 'table-btn-toggle-active'}`}
                                    title={activo ? 'Desactivar asistente con error' : 'Activar asistente'}
                                    onClick={() => handleOpenAsistenteToggle(asistente)}
                                  >
                                    <i className={`fas ${activo ? 'fa-toggle-on' : 'fa-toggle-off'}`}></i>
                                  </button>
                                </div>
                              ) : (
                                <span className="ave-actions-disabled">Sin acciones</span>
                              )
                            )
                          ) : (
                            <span className="muted">Solo lectura</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!asistentesLoading && !asistentesError && filteredAsistentes.length > 0 && (
            renderPagination(filteredAsistentes.length, asistentesPage, setAsistentesPage)
          )}
        </div>
      </div>
    </div>
  );

  const renderAlumnosSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Alumnos</p>
          <h2>Alumnos en {activeRegionalName}</h2>
          <p className="subtitle">Listado filtrable de alumnos asociados a este regional.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-user-graduate"></i>
            </div>
            <div>
              <p className="count-label">Total alumnos</p>
              <p className="count-number">{alumnosTotal}</p>
            </div>
          </div>
          {canManageScopedData && (
            <button className="primary-btn" type="button" onClick={openAlumnoAssociateModal}>
              <i className="fas fa-user-plus" />
              <span>Asociar nuevo alumno</span>
            </button>
          )}
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-user-graduate"></i>
            <span>Listado de alumnos</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre, email o legajo"
                value={alumnosSearch}
                onChange={(e) => { setAlumnosSearch(e.target.value); setAlumnosPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAlumnos} disabled={alumnosLoading}>
              <i className={`fas ${alumnosLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>
        <div className="card-content professors-table">
          {alumnosLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando alumnos de {activeRegionalName}...</p>
            </div>
          )}

          {!alumnosLoading && alumnosError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{alumnosError}</p>
              <button className="refresh-btn" type="button" onClick={loadAlumnos}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!alumnosLoading && !alumnosError && filteredAlumnos.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No se encontraron alumnos que coincidan con tu búsqueda.</p>
            </div>
          )}

          {!alumnosLoading && !alumnosError && filteredAlumnos.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th>Legajo</th>
                    <th>Teléfono</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAlumnos, alumnosPage).map((al) => {
                    const nombre = getAlumnoNombre(al);
                    const email = al.email || al.Email || '-';
                    const legajo = al.legajo || al.Legajo || '-';
                    const telefono = al.telefono || al.Telefono || '-';
                    const activo = isAlumnoActivo(al);

                    return (
                      <tr key={al.id || al.Id || email}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                            {(legajo && legajo !== '-') && <span className="muted">Legajo {legajo}</span>}
                          </div>
                        </td>
                        <td>{email}</td>
                        <td>{legajo}</td>
                        <td>{telefono}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          {canManageScopedData ? (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="action-edit-btn"
                                onClick={() => handleEditAlumno(al)}
                                title="Editar alumno"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className="action-remove-btn"
                                onClick={() => openAlumnoDisassociateModal(al)}
                                title="Desasociar alumno del regional"
                              >
                                <i className="fas fa-link-slash"></i>
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                                onClick={() => openAlumnoToggleModal(al)}
                                disabled={alumnoToggleLoading}
                                title={activo ? 'Desactivar alumno' : 'Activar alumno'}
                              >
                                <i className={`fas ${activo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                              </button>
                            </div>
                          ) : (
                            <span className="muted">Solo lectura</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!alumnosLoading && !alumnosError && alumnosTotal > 0 && (
            renderPagination(alumnosTotal, alumnosPage, setAlumnosPage)
          )}
        </div>
      </div>

      {alumnoAssociateModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Asociar alumno</h3>
            <p>
              Primero validaremos si el alumno está registrado y/o asociado al regional {activeRegionalName}.
            </p>
            <div className="form-field">
              <label>Email del alumno</label>
              <input
                type="email"
                placeholder="alumno@ejemplo.com"
                value={alumnoAssociateEmail}
                onChange={(e) => setAlumnoAssociateEmail(e.target.value)}
              />
            </div>
            {alumnoAssociateMessage && (
              <div className="modal-message">
                {alumnoAssociateMessage}
              </div>
            )}
            <div className="modal-actions">
              {alumnoAssociateSuccess ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={closeAlumnoAssociateModal}
                >
                  <i className="fas fa-check" />
                  <span>Ok</span>
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={closeAlumnoAssociateModal}
                    disabled={alumnoAssociateLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn-primary success"
                    onClick={handleValidateAndAssociateAlumno}
                    disabled={alumnoAssociateLoading}
                  >
                    {alumnoAssociateLoading ? (
                      <i className="fas fa-spinner fa-spin" />
                    ) : (
                      <i className="fas fa-check" />
                    )}
                    <span>Validar y asociar</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {alumnoMissingModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-plus" />
            </div>
            <h3>Alumno no encontrado</h3>
            <p>
              El alumno {alumnoMissingModal.email} no existe en el sistema. ¿Desea continuar con la carga de dicho alumno?
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setAlumnoMissingModal({ open: false, email: '' })}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary success"
                onClick={() => {
                  const emailToUse = alumnoMissingModal.email;
                  setAlumnoMissingModal({ open: false, email: '' });
                  navigate('/admin/alumnos/nuevo', {
                    replace: false,
                    state: {
                      email: emailToUse,
                      idRegional: activeRegionalId
                    }
                  });
                }}
              >
                <i className="fas fa-arrow-right" />
                <span>Continuar con alta</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {alumnoDisassociateModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-unlink" />
            </div>
            <h3>Desasociar alumno</h3>
            <p>
              El alumno {getAlumnoNombre(alumnoDisassociateModal.alumno)} dejará de estar asociado al regional {activeRegionalName}. No podrá ver ni gestionar contenidos de este regional hasta que se lo asocie nuevamente.
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAlumnoDisassociateModal}
                disabled={alumnoDisassociateLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary danger"
                onClick={handleAlumnoDisassociate}
                disabled={alumnoDisassociateLoading}
              >
                {alumnoDisassociateLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-unlink" />}
                <span>Desasociar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {alumnoToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${alumnoToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
            </div>
            <h3>
              {alumnoToggleModal.nextActivo ? 'Activar alumno' : 'Desactivar alumno'}
            </h3>
            <p>
              {alumnoToggleModal.nextActivo
                ? `A continuación se activará el alumno ${getAlumnoNombre(alumnoToggleModal.alumno)}, permitiendo que pueda iniciar sesión en el sistema. ¿Está de acuerdo?`
                : `A continuación se desactivará el alumno ${getAlumnoNombre(alumnoToggleModal.alumno)}, por lo tanto no podrá iniciar sesión en el sistema. ¿Está de acuerdo?`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAlumnoToggleModal}
                disabled={alumnoToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${alumnoToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleAlumno}
                disabled={alumnoToggleLoading}
              >
                {alumnoToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${alumnoToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
                )}
                <span>{alumnoToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAdminAreaSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Admins de área</p>
          <h2>Administradores por regional</h2>
          <p className="subtitle">Gestiona los usuarios administradores de área asociados a este regional.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-user-tie"></i>
            </div>
            <div>
              <p className="count-label">Total admins</p>
              <p className="count-number">{adminAreaTotal}</p>
            </div>
          </div>
          {canManageScopedData && (
            <button className="primary-btn" type="button" onClick={handleCreateAdminArea}>
              <i className="fas fa-user-plus" />
              <span>Agregar admin de área</span>
            </button>
          )}
        </div>
      </div>

      <div className="content-card">
            <div className="card-header">
              <div className="card-title">
                <i className="fas fa-user-tie"></i>
                <span>Listado de admins</span>
              </div>
              <div className="professor-actions">
                <div className="professor-search">
                  <i className="fas fa-search" aria-hidden="true" />
                  <input
                type="text"
                placeholder="Buscar por nombre o email"
                value={adminAreaSearch}
                onChange={(e) => { setAdminAreaSearch(e.target.value); setAdminAreaPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAdminAreaUsers} disabled={adminAreaLoading}>
              <i className={`fas ${adminAreaLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
              </div>
            </div>
        <div className="card-content professors-table">
          {adminAreaLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando admins de área...</p>
            </div>
          )}

          {!adminAreaLoading && adminAreaError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{adminAreaError}</p>
              <button className="refresh-btn" type="button" onClick={loadAdminAreaUsers}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!adminAreaLoading && !adminAreaError && filteredAdminAreaUsers.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay admins de área asociados a este regional.</p>
            </div>
          )}

          {!adminAreaLoading && !adminAreaError && filteredAdminAreaUsers.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Email</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAdminAreaUsers, adminAreaPage).map((u) => {
                    const nombre = u.displayName || u.DisplayName || `${u.nombre || u.Nombre || ''} ${u.apellido || u.Apellido || ''}`.trim() || u.email || u.Email || 'Sin nombre';
                    const email = u.email || u.Email || '-';
                    const activo = isAdminAreaActivo(u);

                    return (
                      <tr key={u.id || u.Id || email}>
                        <td>
                          <div className="prof-name">
                            <span className="name">{nombre}</span>
                          </div>
                        </td>
                        <td>{email}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td className="text-center">
                          {canManageScopedData ? (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="action-edit-btn"
                                onClick={() => handleEditAdminArea(u)}
                                title="Editar admin de área"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                                onClick={() => openAdminAreaToggleModal(u)}
                                disabled={adminAreaToggleLoading}
                                title={activo ? 'Desactivar admin' : 'Activar admin'}
                              >
                                <i className={`fas ${activo ? 'fa-user-slash' : 'fa-user-check'}`}></i>
                              </button>
                            </div>
                          ) : (
                            <span className="muted">Solo lectura</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!adminAreaLoading && !adminAreaError && adminAreaTotal > 0 && (
            renderPagination(adminAreaTotal, adminAreaPage, setAdminAreaPage)
          )}
        </div>
      </div>

      {adminAreaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${adminAreaToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
            </div>
            <h3>
              {adminAreaToggleModal.nextActivo ? 'Activar admin de área' : 'Desactivar admin de área'}
            </h3>
            <p>
              {adminAreaToggleModal.nextActivo
                ? `Se activará el admin ${adminAreaToggleModal.user?.displayName || adminAreaToggleModal.user?.email || ''}.`
                : `Se desactivará el admin ${adminAreaToggleModal.user?.displayName || adminAreaToggleModal.user?.email || ''}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAdminAreaToggleModal}
                disabled={adminAreaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${adminAreaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleAdminArea}
                disabled={adminAreaToggleLoading}
              >
                {adminAreaToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${adminAreaToggleModal.nextActivo ? 'fa-user-check' : 'fa-user-slash'}`} />
                )}
                <span>{adminAreaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderMateriasSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Materias</p>
          <h2>Materias del regional</h2>
          <p className="subtitle">Gestiona las materias asociadas al área para este regional.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-book"></i>
            </div>
            <div>
              <p className="count-label">Total materias</p>
              <p className="count-number">{materiasTotal}</p>
            </div>
          </div>
          {canManageMaterias && (
            <button className="primary-btn" type="button" onClick={handleCreateMateria}>
              <i className="fas fa-plus" />
              <span>Nueva materia</span>
            </button>
          )}
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-book-open"></i>
            <span>Listado de materias</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre, código, año o área"
                value={materiasSearch}
                onChange={(e) => setMateriasSearch(e.target.value)}
              />
            </div>
            <div className="materias-filters-inline">
              <select
                className="materias-filter-select"
                value={materiasFilters.anio}
                onChange={(e) => handleMateriaFilterChange('anio', e.target.value)}
              >
                <option value="">Todos los años</option>
                {materiaYearOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    Año {option.label}
                  </option>
                ))}
              </select>
              <select
                className="materias-filter-select"
                value={materiasFilters.tipo}
                onChange={(e) => handleMateriaFilterChange('tipo', e.target.value)}
              >
                <option value="">Todos los tipos</option>
                {MATERIA_TIPO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                className="materias-filter-select"
                value={materiasFilters.area}
                onChange={(e) => handleMateriaFilterChange('area', e.target.value)}
              >
                <option value="">Todas las áreas</option>
                {materiaAreaOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button type="button" className="materias-clear-btn" onClick={clearMateriaFilters}>
                Limpiar
              </button>
            </div>
            <button className="refresh-btn" type="button" onClick={loadMaterias} disabled={materiasLoading}>
              <i className={`fas ${materiasLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>
        <div className="card-content professors-table">
          {materiasLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando materias...</p>
            </div>
          )}

          {!materiasLoading && materiasError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{materiasError}</p>
              <button className="refresh-btn" type="button" onClick={loadMaterias}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!materiasLoading && !materiasError && filteredMaterias.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay materias cargadas.</p>
            </div>
          )}

          {!materiasLoading && !materiasError && filteredMaterias.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Código</th>
                    <th>Año</th>
                    <th>Tipo</th>
                    <th>Área</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredMaterias, materiasPage).map((m) => {
                    const nombre = getMateriaNombre(m);
                    const codigo = m.codigo || m.Codigo || '-';
                    const anio = m.valorAnio ?? m.ValorAnio;
                    const tipoMateria = getMateriaTipoLabel(m.tipoMateria || m.TipoMateria);
                    const area = m.nombreArea || m.areaNombre || m.NombreArea || m.AreaNombre || m.area || m.Area || '-';
                    const activo = typeof m.activo === 'boolean' ? m.activo : typeof m.Activo === 'boolean' ? m.Activo : true;

                    return (
                      <tr key={m.id || m.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{codigo}</td>
                        <td>{anio ?? 'Sin año'}</td>
                        <td>{tipoMateria}</td>
                        <td>{area}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          {canManageMaterias ? (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="action-edit-btn"
                                onClick={() => handleEditMateria(m)}
                                title="Editar materia"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                                onClick={() => openMateriaToggleModal(m)}
                                disabled={materiaToggleLoading}
                                title={activo ? 'Desactivar materia' : 'Activar materia'}
                              >
                                <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`} />
                              </button>
                            </div>
                          ) : (
                            <span className="muted">Solo lectura</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!materiasLoading && !materiasError && materiasTotal > 0 && (
            renderPagination(materiasTotal, materiasPage, setMateriasPage)
          )}
        </div>
      </div>

      {materiaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${materiaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>
              {materiaToggleModal.nextActivo ? 'Activar materia' : 'Desactivar materia'}
            </h3>
            <p>
              {materiaToggleModal.nextActivo
                ? `Se activará la materia ${getMateriaNombre(materiaToggleModal.materia)}.`
                : `Se desactivará la materia ${getMateriaNombre(materiaToggleModal.materia)}.`}
            </p>
            <p style={{ color: '#475569' }}>Nota: la acción se actualizará contral backend cuando se definan los endpoints.</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeMateriaToggleModal}
                disabled={materiaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${materiaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleMateria}
                disabled={materiaToggleLoading}
              >
                {materiaToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${materiaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
                )}
                <span>{materiaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderAreasSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Áreas</p>
          <h2>Áreas del regional</h2>
          <p className="subtitle">Gestiona las áreas asociadas a este regional.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-sitemap"></i>
            </div>
            <div>
              <p className="count-label">Total áreas</p>
              <p className="count-number">{areasTotal}</p>
            </div>
          </div>
          {canManageScopedData && (
            <button className="primary-btn" type="button" onClick={handleCreateArea}>
              <i className="fas fa-plus" />
              <span>Nueva área</span>
            </button>
          )}
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-sitemap"></i>
            <span>Listado de áreas</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre o descripción"
                value={areasSearch}
                onChange={(e) => { setAreasSearch(e.target.value); setAreasPage(1); }}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadAreas} disabled={areasLoading}>
              <i className={`fas ${areasLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>
        <div className="card-content professors-table">
          {areasLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando áreas...</p>
            </div>
          )}

          {!areasLoading && areasError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{areasError}</p>
              <button className="refresh-btn" type="button" onClick={loadAreas}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!areasLoading && !areasError && filteredAreas.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay áreas cargadas.</p>
            </div>
          )}

          {!areasLoading && !areasError && filteredAreas.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Descripción</th>
                    <th>Admin de área</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {paginate(filteredAreas, areasPage).map((a) => {
                    const nombre = getAreaNombre(a);
                    const descripcion = a.descripcion || a.Descripcion || '-';
                    const admins = a.adminsArea || a.AdminsArea || [];
                    const firstAdmin = admins[0] || null;
                    const adminLabel = firstAdmin ? (firstAdmin.email || firstAdmin.Email || firstAdmin.displayName || firstAdmin.DisplayName || 'Admin de área') : '-';
                    const adminSuffix = admins.length > 1 ? '...' : '';
                    const activo = typeof a.activo === 'boolean' ? a.activo : typeof a.Activo === 'boolean' ? a.Activo : true;

                    return (
                      <tr key={a.id || a.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{descripcion}</td>
                        <td>{adminLabel}{adminSuffix}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          {canManageScopedData ? (
                            <div className="action-buttons">
                              <button
                                type="button"
                                className="action-edit-btn"
                                onClick={() => handleEditArea(a)}
                                title="Editar área"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                                onClick={() => openAreaToggleModal(a)}
                                disabled={areaToggleLoading}
                                title={activo ? 'Desactivar área' : 'Activar área'}
                              >
                                <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`} />
                              </button>
                            </div>
                          ) : (
                            <span className="muted">Solo lectura</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!areasLoading && !areasError && areasTotal > 0 && (
            renderPagination(areasTotal, areasPage, setAreasPage)
          )}
        </div>
      </div>

      {areaToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${areaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>{areaToggleModal.nextActivo ? 'Activar área' : 'Desactivar área'}</h3>
            <p>
              {areaToggleModal.nextActivo
                ? `Se activará el área ${getAreaNombre(areaToggleModal.area)}.`
                : `Se desactivará el área ${getAreaNombre(areaToggleModal.area)}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeAreaToggleModal}
                disabled={areaToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${areaToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleArea}
                disabled={areaToggleLoading}
              >
                {areaToggleLoading ? <i className="fas fa-spinner fa-spin" /> : <i className={`fas ${areaToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />}
                <span>{areaToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  const renderRegionalesSection = () => (
    <div className="profesores-section">
      <div className="section-header">
        <div>
          <p className="eyebrow">Regionales</p>
          <h2>Listado de regionales</h2>
          <p className="subtitle">Administra los regionales disponibles en el sistema.</p>
        </div>
        <div className="profesores-actions-header">
          <div className="profesor-count-card">
            <div className="count-icon">
              <i className="fas fa-globe-americas"></i>
            </div>
            <div>
              <p className="count-label">Total regionales</p>
              <p className="count-number">{regionalesList.length}</p>
            </div>
          </div>
          <button className="primary-btn" type="button" onClick={handleCreateRegional}>
            <i className="fas fa-plus" />
            <span>Nueva regional</span>
          </button>
        </div>
      </div>

      <div className="content-card">
        <div className="card-header">
          <div className="card-title">
            <i className="fas fa-globe"></i>
            <span>Listado de regionales</span>
          </div>
          <div className="professor-actions">
            <div className="professor-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="text"
                placeholder="Buscar por nombre o descripción"
                value={regionalesSearch}
                onChange={(e) => setRegionalesSearch(e.target.value)}
              />
            </div>
            <button className="refresh-btn" type="button" onClick={loadRegionales} disabled={regionalesLoading}>
              <i className={`fas ${regionalesLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            </button>
          </div>
        </div>
        <div className="card-content professors-table">
          {regionalesLoading && (
            <div className="data-table-loading">
              <div className="loading-spinner">
                <i className="fas fa-spinner fa-spin"></i>
              </div>
              <p>Cargando regionales...</p>
            </div>
          )}

          {!regionalesLoading && regionalesError && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-exclamation-triangle"></i>
              </div>
              <p>{regionalesError}</p>
              <button className="refresh-btn" type="button" onClick={loadRegionales}>
                <i className="fas fa-sync-alt" /> Reintentar
              </button>
            </div>
          )}

          {!regionalesLoading && !regionalesError && filteredRegionales.length === 0 && (
            <div className="data-table-empty">
              <div className="empty-icon">
                <i className="fas fa-users-slash"></i>
              </div>
              <p>No hay regionales cargadas.</p>
            </div>
          )}

          {!regionalesLoading && !regionalesError && filteredRegionales.length > 0 && (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>CUIT</th>
                    <th>Descripción</th>
                    <th className="text-center width-sm">Estado</th>
                    <th className="text-center width-sm">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegionales.map((reg) => {
                    const nombre = getRegionalNombre(reg);
                    const cuit = reg.cuit || reg.Cuit || '-';
                    const descripcion = reg.descripcion || reg.Descripcion || '-';
                    const activo = typeof reg.activo === 'boolean' ? reg.activo : typeof reg.Activo === 'boolean' ? reg.Activo : true;

                    return (
                      <tr key={reg.id || reg.Id || nombre}>
                        <td>{nombre}</td>
                        <td>{cuit}</td>
                        <td>{descripcion}</td>
                        <td className="text-center">
                          <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                            {activo ? 'Activa' : 'Inactiva'}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="action-buttons">
                            <button
                              type="button"
                              className="action-edit-btn"
                              onClick={() => handleEditRegional(reg)}
                              title="Editar regional"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              type="button"
                              className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                              onClick={() => openRegionalToggleModal(reg)}
                              disabled={regionalToggleLoading}
                              title={activo ? 'Desactivar regional' : 'Activar regional'}
                            >
                              <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {regionalToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${regionalToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>
              {regionalToggleModal.nextActivo ? 'Activar regional' : 'Desactivar regional'}
            </h3>
            <p>
              {regionalToggleModal.nextActivo
                ? `Se activará el regional ${getRegionalNombre(regionalToggleModal.regional)}.`
                : `Se desactivará el regional ${getRegionalNombre(regionalToggleModal.regional)}.`}
            </p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-secondary"
                onClick={closeRegionalToggleModal}
                disabled={regionalToggleLoading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`btn-primary ${regionalToggleModal.nextActivo ? 'success' : 'danger'}`}
                onClick={handleToggleRegional}
                disabled={regionalToggleLoading}
              >
                {regionalToggleLoading ? (
                  <i className="fas fa-spinner fa-spin" />
                ) : (
                  <i className={`fas ${regionalToggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
                )}
                <span>{regionalToggleModal.nextActivo ? 'Sí, activar' : 'Sí, desactivar'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className={`new-dashboard admin-mode ${isSidebarOpen ? 'sidebar-open' : ''}`}>
      {isSidebarOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)} />}
      {/* Sidebar */}
      <aside className={`dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-university"></i>
            <span>Admin</span>
          </div>
        </div>
        
        <nav className="sidebar-nav">
          <AdminRoleMenuOptions
            user={user}
            isGptClassicEnabled={isGptClassicEnabled}
            isOpenAIAssistantEnabled={isOpenAIAssistantEnabled}
            isRagFlowAssistantEnabled={isRagFlowAssistantEnabled}
            activeItemId={activeMenuItem}
            onSelect={(item) => handleMenuSelect(item.id)}
          />
        </nav>
        
        <div className="sidebar-footer">
          <ActiveRegionalBadge />
          <div className="sidebar-user">
            <div className="user-avatar">
              <i className="fas fa-user-shield"></i>
            </div>
            <div className="user-info">
              <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
              <span className="user-role">Admin</span>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <header className="dashboard-header">
          <div className="header-left">
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setIsSidebarOpen((prev) => !prev)}
              title="Alternar menú lateral"
            >
              <i className="fas fa-bars"></i>
            </button>
            <h1 className="page-title">Dashboard Administrador</h1>
            <p className="page-subtitle">Gestional regional, profesores, alumnos, áreas, materias y asistentes.</p>
          </div>
          <div className="header-right">
            <ActiveRegionalBadge />
            <SolicitudesProcesosBadge />
            <ProcesosMenu />
            <button
              className="logout-btn-header"
              onClick={logout}
              type="button"
              title="Cerrar sesión"
            >
              <i className="fas fa-sign-out-alt" />
              <span>Salir</span>
            </button>
            <div className="user-menu" ref={userMenuRef}>
              <button 
                className="user-menu-trigger"
                onClick={() => setShowUserMenu((prev) => !prev)}
              >
                <div className="user-avatar">
                  <i className="fas fa-user-shield"></i>
                </div>
                <i className="fas fa-chevron-down"></i>
              </button>
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="dropdown-header">
                    <span className="user-name">{user?.displayName || user?.name || user?.email || 'Administrador'}</span>
                    <span className="user-email">{user?.email || ''}</span>
                  </div>
                  <div className="dropdown-divider"></div>
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setPasswordModalOpen(true);
                      setShowUserMenu(false);
                    }}
                  >
                    <i className="fas fa-key"></i>
                    Cambiar contraseña
                  </button>
                  {isGeneralAdmin && (
                    <button
                      className="dropdown-item"
                      onClick={handleBackToSelectRegional}
                    >
                      <i className="fas fa-exchange-alt"></i>
                      Cambiar regional
                    </button>
                  )}
                  <button
                    className="dropdown-item logout-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                  >
                    <i className="fas fa-sign-out-alt"></i>
                    Salir
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="dashboard-content">
          {activeMenuItem === 'resumen' ? (
            dashboardLoading ? (
              <div className="content-card">
                <div className="card-content data-table-loading">
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin"></i>
                  </div>
                  <p>Cargando indicadores del regional...</p>
                </div>
              </div>
            ) : dashboardError ? (
              <div className="content-card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fas fa-exclamation-triangle" />
                    <span>Error al cargar el dashboard</span>
                  </div>
                </div>
                <div className="card-content">
                  <p style={{ color: '#b91c1c' }}>{dashboardError}</p>
                  <button className="primary-btn" type="button" onClick={loadDashboard}>
                    <i className="fas fa-sync-alt" />
                    <span>Reintentar</span>
                  </button>
                </div>
              </div>
            ) : (
              renderDashboardSection()
            )
          ) : activeMenuItem === 'profesores' ? (
            renderProfesoresSection()
          ) : activeMenuItem === 'alumnos' ? (
            renderAlumnosSection()
          ) : activeMenuItem === 'materias' ? (
            renderMateriasSection()
          ) : activeMenuItem === 'areas' ? (
            renderAreasSection()
          ) : activeMenuItem === 'admin-area' ? (
            renderAdminAreaSection()
          ) : activeMenuItem === 'asistentes' || activeMenuItem === 'asistentes-ragflow' ? (
            renderAsistentesSection()
          ) : (
            <div className="content-card">
              <div className="card-header">
                <h2 className="card-title">
                  <i className="fas fa-tools"></i>
                  Contenido en construcción
                </h2>
              </div>
              <div className="card-content">
                <p>Pronto verás métricas y gestiones para Regional, Profesores, Alumnos, Áreas, Materias y Asistentes.</p>
                <p>Menú lateral y estilos replican el look del panel de profesor.</p>
              </div>
            </div>
          )}
        </div>
      </main>

      {passwordModalOpen && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3><i className="fas fa-key" /> Cambiar contraseña</h3>
              <button className="close-btn" onClick={() => { setPasswordModalOpen(false); setPasswordMessage(''); }}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handlePasswordChange} className="prof-create-form">
                <div className="form-field">
                  <label htmlFor="adminCurrentPassword">Contraseña actual</label>
                  <PasswordInputWithToggle
                    id="adminCurrentPassword"
                    value={passwordForm.current}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, current: e.target.value }))}
                    label="contraseña actual"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="adminNewPassword">Nuevo password</label>
                  <PasswordInputWithToggle
                    id="adminNewPassword"
                    value={passwordForm.next}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, next: e.target.value }))}
                    label="nuevo password"
                  />
                </div>
                <div className="form-field">
                  <label htmlFor="adminRepeatPassword">Repetir nuevo password</label>
                  <PasswordInputWithToggle
                    id="adminRepeatPassword"
                    value={passwordForm.repeat}
                    onChange={(e) => setPasswordForm((p) => ({ ...p, repeat: e.target.value }))}
                    label="repetir nuevo password"
                  />
                </div>
                {passwordMessage && (
                  <div className="modal-message" style={{ marginTop: '0.5rem' }}>
                    {passwordLoading && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                    {passwordMessage}
                  </div>
                )}
              </form>
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={() => { setPasswordModalOpen(false); setPasswordMessage(''); }}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handlePasswordChange} disabled={passwordLoading}>
                {passwordLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                <span>Actualizar</span>
              </button>
            </div>
          </div>
        </div>
      )}

      <ChatModal
        isOpen={showChatModal}
        onClose={handleCloseChatModal}
        assistant={selectedAssistant}
      />

      {asistenteToggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-header">
              <h3><i className="fas fa-power-off" /> Cambiar estado del asistente</h3>
              <button className="close-btn" onClick={() => setAsistenteToggleModal({ open: false, asistente: null, nextActivo: true })}>
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="modal-body">
              <p>
                {asistenteToggleModal.nextActivo
                  ? `Se activará el asistente "${asistenteToggleModal.asistente?.nombre || asistenteToggleModal.asistente?.Nombre || 'Asistente'}" para que pueda ser usado.`
                  : `Se desactivará el asistente "${asistenteToggleModal.asistente?.nombre || asistenteToggleModal.asistente?.Nombre || 'Asistente'}" y dejará de estar disponible.`}
              </p>
              {asistenteToggleModal.error && (
                <p style={{ color: '#b91c1c' }}>{asistenteToggleModal.error}</p>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button className="btn btn-secondary" onClick={() => setAsistenteToggleModal({ open: false, asistente: null, nextActivo: true })}>
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleConfirmToggleAsistente} disabled={asistenteToggleLoading}>
                {asistenteToggleLoading ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-check" />}
                <span>Confirmar</span>
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
